// apps/client/src/hooks/useQueryCacheLifecycle.ts
// The two cache-safety rules for dashboard query caching (Tier 3 loading UX).
// Extracted from the QueryCacheLifecycle component so both rules are unit-
// testable with a mock QueryClient.
//
// Rule 1 — auth boundary: when the signed-in uid changes AWAY from a real
//   user (logout, or a direct user switch on a shared office machine) the
//   ENTIRE query cache is cleared. The backend scopes every response by auth
//   claims, so cached data is only valid for the uid that fetched it. Query
//   keys also embed the uid as defense-in-depth; this clear releases the
//   memory and is the primary rule. The null -> uid transition (fresh login)
//   deliberately does NOT clear: nothing can be cached while logged out
//   (queries are uid-gated), and clearing there would cancel the first
//   dashboard fetches mid-flight and double the BigQuery spend on every login.
//   clear() is intentionally client-wide, not dashboard-prefixed: in a
//   financial app, any future cached data is per-user until proven otherwise,
//   so the conservative default at an auth boundary is to drop everything.
//
// Rule 2 — freshness boundary: when an aggregation run completes (the
//   companies/{id}/aggregation_triggers watcher flips isAggregating
//   true -> false), every dashboard query is HARD RESET (resetQueries, not
//   invalidateQueries): active queries drop to their loading state and
//   refetch, so post-upload dashboards show skeletons for the refetch window
//   rather than silently displaying pre-upload money figures as current.
//   Gated to non-'legacy' modes: legacy tenants have no cache to go stale
//   (staleTime/gcTime 0) and the bespoke hooks never refetched on aggregation
//   completion either — firing there would only add ~8 concurrent BigQuery
//   scans per upload for no user-visible change.
//   Known accepted edges: a transient Firestore listener error mid-run flips
//   the watcher false and causes one spurious reset (cost: one refetch round);
//   if the listener stays dead through a completion the reset is missed and
//   staleness is bounded by the 5-minute swr staleTime.
import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { DASHBOARD_QUERY_PREFIX } from '../lib/queryClient';
import type { DashboardCacheMode } from '../lib/queryClient';

export function useQueryCacheLifecycle(
  client: QueryClient,
  uid: string | null,
  isAggregating: boolean,
  cacheMode: DashboardCacheMode
): void {
  // Rule 1: clear everything when a REAL user logs out or switches.
  const prevUidRef = useRef<string | null>(uid);
  useEffect(() => {
    const prevUid = prevUidRef.current;
    if (prevUid !== uid) {
      prevUidRef.current = uid;
      if (prevUid !== null) {
        client.clear();
      }
    }
  }, [client, uid]);

  // Rule 2: hard-reset dashboard queries when aggregation completes.
  // Only the true -> false transition resets; mounting while idle, or an
  // aggregation starting, must not (the pre-aggregation numbers are still
  // the correct ones until the run finishes).
  const prevAggregatingRef = useRef<boolean>(isAggregating);
  useEffect(() => {
    const wasAggregating = prevAggregatingRef.current;
    prevAggregatingRef.current = isAggregating;
    if (wasAggregating && !isAggregating && cacheMode !== 'legacy') {
      client.resetQueries({ queryKey: [DASHBOARD_QUERY_PREFIX] });
    }
  }, [client, isAggregating, cacheMode]);
}
